/**
 * Dynamic Template Provider — the "Vorlagen" section of the planning band
 * (MD-148, Phase 2: drag to create).
 *
 * Renders as one section of the calendar's unified planning shelf, left of
 * the Offene Termine / Ablage sections. Presents intelligently-derived
 * "handles" for appointments that still need scheduling on the selected
 * patient. Each handle is a FullCalendar external draggable (the band owns
 * the single Draggable registration): dropping it on a calendar slot creates
 * AND schedules the appointment in one request. The manual Rx picker lives in
 * the section HEADER, so the only draggable objects in the section are the
 * handles themselves. Vocabulary — handle / provisions / bare / primed /
 * bundle / precedent / suggestion — is defined in docs/planning-handles.md.
 */

import { useEffect, useState } from "react";
import {
  Box,
  Fade,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpRounded from "@mui/icons-material/HelpRounded";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MapIcon from "@mui/icons-material/Map";
import { observer } from "mobx-react";
import ShelfSection from "../EventShelf/ShelfSection";
import { TemplateHandle } from "./types";
import { RxDto, TemplateWidgetDefaultSelectionDto } from "../../../api-client";
import useStore from "../../../helpers/useStore";
import { supplementsFirst } from "../../../helpers/slotTherapy";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { cardShell } from "../../cardShell";
import { VisitTypeBadge } from "../../../atoms/Chip";

interface DynamicTemplateProviderProps {
  handles: TemplateHandle[];
  patientId?: number;
  /** When true, render the manual Rx/therapy picker in the section header. */
  showManualPicker?: boolean;
  /** Selectable (non-BOOKED) Rxs for the manual picker. */
  selectableRxs?: RxDto[];
  /** Pre-selection derived from the most recently scheduled timeslot. */
  defaultSelection?: TemplateWidgetDefaultSelectionDto | null;
  /** Builds an on-the-fly handle from a manual Rx + therapyRx selection. */
  buildManualHandle?: (rxId: string, therapyRxId: string) => TemplateHandle | null;
  /** Precedent-derived SUGGESTION bundles for the picked Rx. */
  buildManualBundles?: (rxId: string) => TemplateHandle[];
  /** Widget summoned state (manual mode) — drives the header toggle button. */
  open?: boolean;
  /** Summon/dismiss the manual picker (replaces the old floating action button). */
  onToggleOpen?: () => void;
  /** Fixed card-row height dictated by the band. */
  rowHeight: number;
  /** Column-band mode (shelf in the drawer): chips wrap instead of scrolling. */
  wrap?: boolean;
}

/** Convert a total number of minutes into the FullCalendar "HH:mm:ss" string. */
const minutesToDuration = (minutes: number): string => {
  const safe = Math.max(minutes, 0);
  const hh = Math.floor(safe / 60)
    .toString()
    .padStart(2, "0");
  const mm = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${hh}:${mm}:00`;
};

/**
 * Build the `data-event` payload FullCalendar's Draggable reads on drop. The
 * payload carries an `isTemplate` marker plus the template descriptor needed
 * by the create-and-schedule endpoint, and a synthesized `timeSlots` array so
 * the existing therapist-credential / invalid-type validations keep working.
 *
 * Exported for the keyboard workflow (calendarHotkeys): "a" source-cycling
 * selects the first handle by storing exactly this payload.
 */
export const buildDataEvent = (handle: TemplateHandle, patientId?: number) => {
  // The handle displays therapies in prescription order ("KG + PA"), but the
  // SCHEDULING order puts supplements first — the server lays slots out in the
  // order sent here, and a PA landing after the KG is the exact complaint this
  // rule answers (see supplementsFirst).
  const schedulingOrder = supplementsFirst(handle.therapies, (t) => t.isSupplementary);

  /**
   * How much time the drop claims AT the drop point — which is what the drag
   * ghost draws and what the calendar measures the target gap against.
   *
   * Passive preparations that LEAD the appointment (Warmpackung, Heißluft) run
   * BEFORE the drop point: `createAndSchedule` shifts them back by
   * `passiveLeadShiftMinutes`, so the first therapy the therapist performs
   * begins exactly on the drop. Counting them here made a KG+PA template drag
   * as 45 minutes and claim a 45-minute gap while occupying 20 — the same
   * defect MD-314 fixed for shelf and Ablage drops, which state this rule in
   * `layoutSlots`. Only the LEADING run is excluded: a passive application
   * after an active therapy is where someone put it on purpose, and an
   * all-passive handle has nothing to lead into, so it keeps its full length.
   */
  const firstActive = schedulingOrder.findIndex((t) => !t.isHeatTreatment);
  const atDropPoint = firstActive < 0 ? schedulingOrder : schedulingOrder.slice(firstActive);
  const totalMinutes = atDropPoint.reduce((acc, t) => acc + (t.duration || 0), 0);
  return {
    isTemplate: true,
    id: `template:${handle.id}`,
    title: handle.label,
    duration: minutesToDuration(totalMinutes),
    patient: patientId != null ? { id: patientId } : undefined,
    // Synthesized slots for the calendar's validation pipeline (credentials,
    // invalid-type checks).
    timeSlots: schedulingOrder.map((t) => ({
      therapyRx: {
        id: t.therapyRxId,
        therapy: {
          id: t.therapyId,
          abbreviation: t.abbreviation,
          duration: t.duration,
        },
      },
    })),
    // Descriptor consumed by the create-and-schedule drop handler.
    template: {
      rxId: handle.rxId,
      patientId,
      kind: handle.kind,
      therapyRxIds: schedulingOrder.map((t) => t.therapyRxId),
      // Inference results — present when the drop can proceed without a dialog.
      addressId: handle.inferredAddressId,
      visitFees: handle.inferredVisitFees,
      needsInput: handle.needsInput,
    },
  };
};

/**
 * The fee abbreviation for the handle's badge — only when it is one decision:
 * a single therapy's fee, or a bundle whose therapies share one fee. Mixed
 * bundles show no tag on the badge; the tooltip lists them per therapy.
 */
const singleFeeLabel = (handle: TemplateHandle): string | undefined => {
  const labels = handle.therapies
    .map((t) => handle.inferredVisitFeeLabels?.[t.therapyRxId])
    .filter((l): l is string => l !== undefined);
  if (labels.length === 0) return undefined;
  const unique = Array.from(new Set(labels));
  return unique.length === 1 ? unique[0] : undefined;
};

/**
 * May this handle offer the "nächstgelegene Termine" search?
 *
 * Only a PRIMED handle can: the search orbits an address, and a bare handle has
 * none yet (its address is one of the things the drop dialog will ask for). The
 * Haus/Heim restriction is the same one the shelf cards apply — closest-address
 * search is a home-visit routing tool, and the server excludes practice
 * addresses from the results anyway, so a Praxis-primed handle would only ever
 * list other people's house calls near the practice.
 */
export const canSearchClosest = (handle: TemplateHandle): boolean =>
  !!handle.inferredAddressId &&
  (handle.inferredVisitType === "Haus" || handle.inferredVisitType === "Heim");

/** Format an ISO date as German short date (DD.MM.YYYY); empty for invalid. */
const formatRxDate = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const HandleChip = observer(({ handle, patientId }: { handle: TemplateHandle; patientId?: number }) => {
  // Click-to-select for the keyboard workflow: the selected chip's FULL drag
  // payload is stored so "b"/"v" on a calendar cell can plan it exactly like a
  // drag-drop. Clicking again deselects. The selection deliberately SURVIVES
  // each insert (a template has no per-appointment identity), so the chip owns
  // the selection lifecycle:
  const CalendarStore = useStore(CalendarContext);
  const dataEvent = buildDataEvent(handle, patientId);
  const isSelected = CalendarStore.selectedShelfTemplate?.id === dataEvent.id;

  // 1) Refresh the stored payload when the handle's data changes while selected
  //    (e.g. the address / visit fees become inferable after the first drop).
  //    Cheap: runs only on chip re-renders, which only happen on handle changes.
  const payloadJson = JSON.stringify(dataEvent);
  useEffect(() => {
    if (!isSelected) return;
    if (JSON.stringify(CalendarStore.selectedShelfTemplate) !== payloadJson) {
      CalendarStore.setSelectedShelfTemplate(JSON.parse(payloadJson));
    }
  }, [isSelected, payloadJson, CalendarStore]);

  // 2) Clear the selection when the selected chip disappears (Rx fully planned,
  //    patient switch, widget hidden, manual pick changed) — a stale payload
  //    must not remain insertable via b/v. Chips are keyed by handle.id, so
  //    unmount is exactly "this handle no longer exists".
  useEffect(() => {
    const chipId = dataEvent.id;
    return () => {
      if (CalendarStore.selectedShelfTemplate?.id === chipId) {
        CalendarStore.setSelectedShelfTemplate(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const durationLabel = handle.therapies
    .filter((t) => !t.isHeatTreatment)
    .reduce((acc, t) => acc + t.duration, 0);

  const rxDateLabel = formatRxDate(handle.rxDate);
  const rxHeader = [
    handle.rxNumber ? `Rezept ${handle.rxNumber}` : "Rezept —",
    rxDateLabel,
  ]
    .filter(Boolean)
    .join(" · ");

  const tooltip = (
    <Box sx={{ maxWidth: 260 }}>
      <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
        {rxHeader}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {handle.label}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
        {handle.remaining} offen{durationLabel ? ` · ${durationLabel} min` : ""}
      </Typography>
      {handle.provenance === "precedent" && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
          Vorschlag: Kombination wie in bisherigen Terminen
        </Typography>
      )}
      {/* The provisions themselves, not a sentence about them: what was
          copied from precedent is exactly what the user wants to verify
          before dropping. */}
      {(handle.inferredVisitType || handle.inferredAddressLabel) && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
          Ort: {[handle.inferredVisitType, handle.inferredAddressLabel].filter(Boolean).join(" · ")}
        </Typography>
      )}
      {handle.inferredVisitFeeLabels &&
        Object.keys(handle.inferredVisitFeeLabels).length > 0 && (
          <Typography variant="caption" sx={{ display: "block" }}>
            Pauschale:{" "}
            {handle.therapies
              .filter((t) => handle.inferredVisitFeeLabels![t.therapyRxId] !== undefined)
              .map((t) =>
                handle.therapies.length > 1
                  ? `${t.abbreviation}: ${handle.inferredVisitFeeLabels![t.therapyRxId]}`
                  : handle.inferredVisitFeeLabels![t.therapyRxId]
              )
              .join(", ")}
          </Typography>
        )}
      {handle.needsInput && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}>
          Fehlende Angaben werden beim Ablegen abgefragt
        </Typography>
      )}
    </Box>
  );

  const isCombined = handle.kind === "combined";
  // Same floating action the shelf cards carry, under the same rule: only on
  // the SELECTED item, and only when there is a home-visit address to search
  // around. A bare handle has no address yet, so it gets no map icon.
  const showClosestAction = isSelected && canSearchClosest(handle);

  return (
    // Relative wrapper so the map action can sit OUTSIDE the `fc-event` chip.
    // Inside it, FullCalendar's Draggable would claim the button's mousedown
    // (it has no ignore-selector for interactive elements) — which is exactly
    // why the shelf keeps its action a sibling of the DragSurface too.
    <Box sx={{ position: "relative", display: "flex", flex: "0 0 auto" }}>
      <Tooltip title={tooltip} placement="top" arrow>
        {/* The WHOLE chip is the drag surface (fc-event + data-event), matching
            the shelf cards after the drag-zone inversion — no grip rail, no
            divider. A plain click (no drag) still toggles the selection. */}
        <Box
          className={`fc-event shelf-drag-source ${cardShell()}`}
          data-event={JSON.stringify(dataEvent)}
          onClick={() => CalendarStore.setSelectedShelfTemplate(isSelected ? null : dataEvent)}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "stretch",
            flex: "0 0 auto",
            cursor: "grab",
            // State on the EDGE, surface stays white (design-system rule 4):
            // a bundle carries the secondary hue on its border; a BARE handle —
            // one whose drop will open the completion dialog — is dashed, the
            // conventional mark for "unfinished". Primed handles are solid.
            borderColor: isCombined ? "secondary.main" : undefined,
            borderStyle: handle.needsInput ? "dashed" : undefined,
            boxShadow: isSelected ? "0 0 0 2px #007bff" : undefined,
          }}
        >
          {/* Revealed only inside FullCalendar's drag-mirror clone. */}
          <span className="shelf-drag-ghost">
            {handle.label}
            {handle.rxNumber ? ` · Rz ${handle.rxNumber}` : ""}
          </span>
          {/* Remaining count — INSIDE the card's top-right corner (same pattern
              as the shelf cards' group count). An overhanging MUI Badge would be
              clipped by the row's overflow scroll — and no, z-index can't save
              it: overflow clipping ignores stacking order — so keeping it
              internal also spares the row the compensating padding. Hidden at 0,
              matching the old Badge's auto-hide. */}
          {handle.remaining > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                zIndex: 3,
                minWidth: 14,
                height: 14,
                px: 0.4,
                borderRadius: "7px",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontSize: "0.6rem",
                fontWeight: 700,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              {Math.min(handle.remaining, 99)}
            </Box>
          )}
          {/* Rx identity + therapy label. Right padding keeps the nowrap first
              line clear of the corner count. */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              pl: 1,
              pr: 2.5,
              py: 0.5,
              minWidth: 74,
            }}
          >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap" }}
              >
                {handle.rxNumber ? `Rezept ${handle.rxNumber}` : "Rezept —"}
              </Typography>
              {rxDateLabel && (
                <Typography
                  variant="caption"
                  sx={{ lineHeight: 1.2, opacity: 0.8, whiteSpace: "nowrap" }}
                >
                  {rxDateLabel}
                </Typography>
              )}
            {/* Therapy abbreviation(s). */}
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.3, whiteSpace: "nowrap" }}
            >
              {handle.label}
            </Typography>
          </Box>
          {/* Provision state as DATA, not just border style: a primed handle
              shows the visit-type icon (+ fee tag) it will schedule with, the
              same badge the slot rows use; a bare one shows the open question.
              Details are on the tooltip. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 20,
              flexShrink: 0,
              pr: 0.5,
              "& .MuiSvgIcon-root": { fontSize: 16 },
            }}
          >
            {handle.needsInput ? (
              <HelpRounded sx={{ color: "var(--ink-text-muted)", opacity: 0.7 }} />
            ) : (
              <VisitTypeBadge
                visitType={handle.inferredVisitType ?? ""}
                visitFee={singleFeeLabel(handle)}
              />
            )}
          </Box>
        </Box>
      </Tooltip>
      {showClosestAction && (
        <Fade in={showClosestAction}>
          <Tooltip
            title={
              CalendarStore.continuousView
                ? "Nächstgelegene Dauertermine"
                : "Nächstgelegene Termine"
            }
            placement="top"
          >
            {/* Right of 22px, not 2: the corner count owns the top-right and the
                visit-type badge owns the right-hand column, so the action tucks
                into the text column's reserved right padding instead of sitting
                on top of either. */}
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                CalendarStore.openClosestAppointments({
                  // A handle has no appointment of its own yet — its address IS
                  // the target, which is what "primed" buys us here.
                  addressId: handle.inferredAddressId,
                  scope: CalendarStore.continuousView ? "continuous" : "appointments",
                });
              }}
              sx={{
                position: "absolute",
                bottom: 2,
                right: 22,
                zIndex: 10,
                bgcolor: "secondary.main",
                color: "secondary.contrastText",
                boxShadow: 2,
                width: 20,
                height: 20,
                "&:hover": { bgcolor: "secondary.dark" },
              }}
            >
              <MapIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Fade>
      )}
    </Box>
  );
});

const DynamicTemplateProvider: React.FC<DynamicTemplateProviderProps> = observer(
  ({
    handles,
    patientId,
    showManualPicker = false,
    selectableRxs = [],
    defaultSelection = null,
    buildManualHandle,
    buildManualBundles,
    open = false,
    onToggleOpen,
    rowHeight,
    wrap,
  }) => {
    // Manual picker selection: an Rx only. There is deliberately NO therapy
    // dropdown — every therapy of the picked Rx becomes its own draggable
    // handle immediately, so the choice IS the drag. (The dropdown also forced an
    // extra click per schedule and hid the other therapies.)
    const [manualRxId, setManualRxId] = useState<string>("");

    // Seed or self-heal the Rx pick: prefer the default selection (most
    // recently scheduled slot), else the latest Rx. Also reseeds when the
    // current pick is no longer selectable (e.g. after a patient switch —
    // this component stays mounted in the band now).
    useEffect(() => {
      if (!showManualPicker || selectableRxs.length === 0) return;
      if (manualRxId && selectableRxs.some((r) => String(r.id) === manualRxId)) return;
      const defRx =
        (defaultSelection?.rxId &&
          selectableRxs.find((r) => String(r.id) === String(defaultSelection.rxId))) ||
        selectableRxs[0];
      setManualRxId(String(defRx.id));
    }, [showManualPicker, selectableRxs, defaultSelection, manualRxId]);

    const selectedManualRx = selectableRxs.find((r) => String(r.id) === manualRxId);
    // One handle per therapy of the picked Rx, in prescription order. Selection
    // mode is additive to the auto handles, so a therapy can coincide with an
    // auto chip (same single therapy of the same Rx) — don't render the
    // identical draggable twice.
    const manualHandles =
      showManualPicker && manualRxId && buildManualHandle
        ? ((selectedManualRx?.therapyRxs ?? []) as any[])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((trx) => buildManualHandle(manualRxId, String(trx.id)))
            .filter((h): h is NonNullable<typeof h> => !!h)
            .filter(
              (h) =>
                !handles.some(
                  (a) =>
                    a.kind === "single" &&
                    a.rxId === h.rxId &&
                    a.therapies[0]?.therapyRxId === h.therapies[0]?.therapyRxId
                )
            )
        : [];

    // Suggestion bundles for the picked Rx, deduped against auto chips by id
    // (shared `:combined:` id scheme — see buildManualBundles).
    const autoIds = new Set(handles.map((h) => h.id));
    const manualBundles =
      showManualPicker && manualRxId && buildManualBundles
        ? buildManualBundles(manualRxId).filter((h) => !autoIds.has(h.id))
        : [];

    const count = handles.length + manualHandles.length + manualBundles.length;

    // Compact selects sized for the 28px section-header line.
    const headerSelectSx = {
      fontSize: "0.75rem",
      height: 24,
      backgroundColor: "background.paper",
      "& .MuiSelect-select": { py: 0.25 },
    } as const;

    const headerExtras = (
      <>
        {showManualPicker &&
          (selectableRxs.length === 0 ? (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Keine Rezepte
            </Typography>
          ) : (
            <>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={manualRxId}
                  onChange={(e) => setManualRxId(String(e.target.value))}
                  displayEmpty
                  sx={headerSelectSx}
                >
                  {selectableRxs.map((rx) => {
                    const dateLabel = formatRxDate(rx.date ? String(rx.date) : undefined);
                    return (
                      <MenuItem key={rx.id} value={String(rx.id)} sx={{ fontSize: "0.75rem" }}>
                        {rx.rxNumber ? `Rezept ${rx.rxNumber}` : "Rezept —"}
                        {dateLabel ? ` · ${dateLabel}` : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </>
          ))}
        {/* Manual-mode controls. Selection mode is ADDITIVE to the auto
            handles: › switches it on (scenario 3 — handles shown, selectors
            hidden), ✕ switches it off again. With no handles the ✕ also
            dismisses the whole section. */}
        {!open && onToggleOpen && (
          <Tooltip title="Manuelle Auswahl (Rezept & Therapie)" placement="top" arrow>
            <IconButton
              size="small"
              onClick={onToggleOpen}
              sx={{ p: 0.25, opacity: 0.55, "&:hover": { opacity: 1 } }}
            >
              <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {open && onToggleOpen && (
          <Tooltip title="Manuelle Auswahl ausblenden" placement="top" arrow>
            <IconButton size="small" onClick={onToggleOpen} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </>
    );

    return (
      <ShelfSection title="Vorlagen" headerExtras={headerExtras} rowHeight={rowHeight} wrap={wrap}>
        {handles.map((handle) => (
          <HandleChip key={handle.id} handle={handle} patientId={patientId} />
        ))}
        {manualBundles.map((handle) => (
          <HandleChip key={`manual-${handle.id}`} handle={handle} patientId={patientId} />
        ))}
        {manualHandles.map((handle) => (
          <HandleChip key={`manual-${handle.id}`} handle={handle} patientId={patientId} />
        ))}
        {count === 0 && (
          <Typography variant="caption" sx={{ opacity: 0.7, alignSelf: "center" }}>
            Keine Vorlagen
          </Typography>
        )}
      </ShelfSection>
    );
  }
);

DynamicTemplateProvider.displayName = "DynamicTemplateProvider";

export default DynamicTemplateProvider;
